import { Button, Grid, Modal, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useIdleTimer, workerTimers } from 'react-idle-timer';
import styled from 'styled-components';
import timeoutImg from '../../../assets/image/timeout.png';
import { Spacer } from '../../common/components/layout';
import {
  useAuthenticationServiceContext,
  useDomainContext,
  useLatestPreferencesContext,
  useLocalStorageExecutorRoleServiceContext,
} from '../../common/v2/context/contexts';

const StyledPaper = styled(Paper)`
  width: 100%;
  padding: 5px;
  white-space: pre-line;
`;

const StyledButton = styled(Button)`
  border-radius: 30px;
  margin-bottom: 1em;
`;

const StyledTypography = styled(Typography)`
  padding-bottom: 5%;
`;

const CountdownTypography = styled(Typography)`
  font-weight: 800;
  font-size: 1.5em;
`;

const Blur = styled.div`
  height: 100vh;
  width: 100vw;
  backdrop-filter: blur(2px);
  background: rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1200;
  pointer-events: none;
`;

export const IdleTimerGuard: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  return (
    <>
      <LogoutModal />
      {children}
    </>
  );
};

const LogoutModal = () => {
  const latestDomainPreferences = useLatestPreferencesContext();
  const timeout =
    (latestDomainPreferences.autoLogoutTimeInMinutes ?? 20) * 60000;
  const promptBeforeIdle =
    (latestDomainPreferences.warningToLogoutTimeInMinutes ?? 1) * 6000;
  const domain = useDomainContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();

  const authService = useAuthenticationServiceContext();

  const [remaining, setRemaining] = useState(timeout);
  const [open, setOpen] = useState(false);

  const { t } = useTranslation();

  const onIdle = () => {
    if (!authService.isSaml) {
      /**
       * When switching from role X to a different role (such as superadmin),
       * it's important to remove the executor role for X from local storage.
       * Otherwise, the app will retain the executor role for X after reloading,
       * which may not be the desired behavior. By removing the executor role for X,
       * the app will reset to the default view for the new role (e.g. the superadmin view).
       * */
      localStorageExecutorRoleService.clear();
      void authService.logOutUser(domain);
    }
    setOpen(false);
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    setOpen(true);
    if (authService.isSaml) {
      pause();
    }
  };

  const { getRemainingTime, activate, isPrompted, pause } = useIdleTimer({
    onIdle,
    onPrompt,
    onActive,
    timeout,
    promptBeforeIdle,
    throttle: 500,
    timers: workerTimers,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      requestIdleCallback(() =>
        setRemaining(Math.ceil(getRemainingTime() / 1000))
      );
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      {authService.isSaml && isPrompted() ? <Blur /> : null}
      <Modal
        open={open}
        onClose={activate}
        sx={{ width: '40vw', margin: '0 auto', top: '33%' }}
      >
        <StyledPaper>
          <Grid item container xs={12}>
            <Grid item xs={5} style={{ padding: 10 }}>
              <img src={timeoutImg} style={{ width: '100%' }} />
            </Grid>
            <Grid
              item
              xs={7}
              style={{
                padding: '1.5em 1em',
              }}
            >
              <StyledTypography variant="h6">
                {<Trans i18nKey="common:snackbarMessages.userLogoutTitle" />}
              </StyledTypography>
              {!authService.isSaml ? (
                <Grid
                  container
                  sx={{
                    height: '80%',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h6">
                    {
                      <Trans i18nKey="common:snackbarMessages.sessionPausedDescription" />
                    }
                  </Typography>
                </Grid>
              ) : (
                <>
                  <StyledTypography>
                    {
                      <Trans i18nKey="common:snackbarMessages.userLogoutDescription" />
                    }
                  </StyledTypography>
                  <Typography>
                    {
                      <Trans i18nKey="common:snackbarMessages.userLogoutWarning" />
                    }
                  </Typography>
                  <CountdownTypography>
                    {t('common:snackbarMessages.userLogoutTimeLeft', {
                      timeLeft: remaining,
                    })}
                  </CountdownTypography>
                </>
              )}
            </Grid>
          </Grid>
          <Spacer />
          <Grid
            item
            container
            xs={12}
            sx={{
              justifyContent: 'space-around',
            }}
          >
            <Grid item xs={5}>
              <StyledButton
                variant="contained"
                color="primary"
                fullWidth
                onClick={activate}
              >
                <Trans
                  i18nKey={`common:snackbarButtons.${authService.isSaml ? 'continuePausedSession' : 'stayLoggedIn'}`}
                />
              </StyledButton>
            </Grid>
          </Grid>
        </StyledPaper>
      </Modal>
    </>
  );
};
